import { CommonSelect, CstmPagination, RequiredFieldAstrick } from "components/Common/Common";
import LoadingOverlay from "components/shared/LoadingOverlay";
import TableLayout from "components/TableLayout";
import { GRADE_OPTIONS } from "config/constants";
import { CLASSROOM_SERVICES } from "config/paths";
import { Formik } from "formik";
import { convertToMomentDate, errorMessageToast, successMessageToast, userFullName } from "helpers/common";
import { cloneDeep, debounce, isEmpty } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useLazyGetSchoolClientsListQuery } from "store/services/clients/school.client";
import { useLazyAssignedSchoolsLookupQuery, useLazySchoolLookupQuery, useLazyTeacherLookupWithSchoolQuery } from "store/services/utility";
import addClassroomConsultationSchema from "./addConsultation.schema";
import AppInputField from "components/shared/FormComponents/AppInputField";
import styles from "./addForm.module.scss";
import { useAddClassroomConsultationMutation, useGetClassroomConsultationDetailQuery, useUpdateClassroomConsultationMutation } from "store/services/classroom";
import LoadingIndicator from "components/shared/LoadingIndicator";
import { genericFormErrorMsg } from "utils/validation/validationHelpers";

const AddClassRoomConsultation = () => {

    const { id: consultationId } = useParams();
    const editMode = !!consultationId;

    const navigate = useNavigate();

    // consultation details
    const { data: consultationDetails, isLoading } = useGetClassroomConsultationDetailQuery(consultationId, { skip: !consultationId });

    const [initialValues, setInitialValues] = useState({ title: "", school: "", grade: "", teacher_id: "" })
    const [schoolOptions, setSchoolOptions] = useState([]);
    const [teachersOptions, setTeachersOptions] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [filter, setFilter] = useState({ search: "", school: "", grade: "", status: "", sort: { column: "id", direction: "desc" } });
    const [pageSize, setPageSize] = useState(10);

    // School Lookup
    const [getSchoolList, { isFetching: getSchoolListFetching }] = useLazyAssignedSchoolsLookupQuery();

    // Teacher Lookup
    const [getTeacherList, { isFetching: getTeacherListFetching }] = useLazyTeacherLookupWithSchoolQuery();

    // school Clients 
    const [createClassroomConsultation, { isLoading: createClassroomConsultationLoading }] = useAddClassroomConsultationMutation();
    const [updateClassroomConsultation, { isLoading: updateClassroomConsultationLoading }] = useUpdateClassroomConsultationMutation();

    const getSchoolOptions = async (search) => {
        try {

            const result = await getSchoolList(search);
            if (result?.data) setSchoolOptions(result?.data?.map(i => ({ label: i.name, value: i.id })));

        } catch (error) { console.log("Error Getting school options"); };

    };

    const getTeacherOptions = async (schoolId) => {
        try {

            const result = await getTeacherList(schoolId);
            if (result?.data?.records?.length) setTeachersOptions(result?.data?.records?.map(i => ({ label: userFullName(i?.client_guardian), value: i?.client_guardian?.id })));

        } catch (error) { console.log("Error Getting teacher options", error); };
    };

    const handleFilterChange = (key) => (value) => {
        setFilter((prevState) => ({
            ...prevState,
            [key]: value,
        }));
        setCurrentPage(1);
    };

    const handleSearch = useCallback(debounce((value) => {
        getSchoolOptions(value);
    }, 500), []);

    useEffect(() => {
        getSchoolOptions();
    }, []);

    useEffect(() => {
        if (consultationDetails) {
            setInitialValues({
                title: consultationDetails?.title,
                school: { value: consultationDetails?.school, label: consultationDetails?.PreflightSchool?.name },
                grade: GRADE_OPTIONS?.find(x => x.value == consultationDetails?.grade),
                teacher_id: { value: consultationDetails?.teacher_id, label: userFullName(consultationDetails?.PreflightTeacher) },
            });
            setFilter({ ...filter, school: consultationDetails?.school, grade: consultationDetails?.grade });
        };
    }, [consultationDetails]);

    const handleSubmit = async (payload) => {
        try {
            let values = cloneDeep(payload);
            values.school = payload?.school?.value;
            values.grade = payload?.grade?.value || undefined;
            values.teacher_id = payload?.teacher_id?.value

            const result = editMode ? await updateClassroomConsultation({ consultationId, data: values }) : await createClassroomConsultation(values);
            if (result?.error) return;

            successMessageToast(result?.data?.message);
            navigate(CLASSROOM_SERVICES);

        } catch (err) { console.log("Error Adding consultation", err); };
    };

    return (
        <>
            <Container fluid>
                <section className={`${styles.addForm} client position-relative py-4`}>

                    {isLoading ? <LoadingIndicator /> :
                        (
                            <Row>
                                <div className="TableFilter d-flex align-items-center justify-content-between gap-10 flex-wrap">
                                    <div className="left">
                                        <ul className="list-unstyled ps-0 mb-0 d-flex align-items-center">
                                            <li className="">
                                                <Link
                                                    className="themeBlue fw-sbold"
                                                    style={{ textDecoration: "underline" }}
                                                    to={CLASSROOM_SERVICES}
                                                >
                                                    Classroom Consultation
                                                </Link>
                                            </li>
                                            <li className="">
                                                <h5 className="m-0 fw-bold themeBlue">
                                                    / {editMode ? "Edit Classroom Consultation" : "Add New Classroom Consultation"}
                                                </h5>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                                <Col lg="12" className="my-2">
                                    <Formik
                                        initialValues={initialValues}
                                        validationSchema={addClassroomConsultationSchema}
                                        onSubmit={handleSubmit}
                                        enableReinitialize
                                    >
                                        {({ handleSubmit, values, submitCount, errors }) => (
                                            <Form onSubmit={handleSubmit}>
                                                <Row className="mb-3">
                                                    <Col md="3" sm="6" className="my-2">
                                                        <label
                                                            htmlFor=""
                                                            className="form-label themeBlue fw-bold mb-0"
                                                        >
                                                            Title
                                                            <RequiredFieldAstrick />
                                                        </label>
                                                        <AppInputField
                                                            type="text"
                                                            name="title"
                                                            placeholder="Title"
                                                            className={`${styles.formControl} form-control`}
                                                            maxLength={50}
                                                        />
                                                    </Col>
                                                    <Col md="3" sm="6" className="my-2">
                                                        <label
                                                            htmlFor=""
                                                            className="form-label themeBlue fw-bold mb-0"
                                                        >
                                                            School
                                                            <RequiredFieldAstrick />
                                                        </label>
                                                        <CommonSelect
                                                            name={"school"}
                                                            placeholder={consultationDetails?.PreflightSchool?.name || schoolOptions?.find(x => x.id == filter?.school)?.name || "Select School"}
                                                            option={schoolOptions || []}
                                                            value={filter?.school}
                                                            onChange={(selected) => {
                                                                handleFilterChange("school")(selected);
                                                                setTeachersOptions([]);
                                                                getTeacherOptions(selected?.value)
                                                            }}
                                                            onInputChange={handleSearch}
                                                            isLoading={getSchoolListFetching}
                                                            isDisabled={editMode}
                                                        />
                                                    </Col>
                                                    <Col md="3" sm="6" className="my-2">
                                                        <label
                                                            htmlFor=""
                                                            className="form-label themeBlue fw-bold mb-0"
                                                        >
                                                            Teacher
                                                            <RequiredFieldAstrick />
                                                        </label>
                                                        <CommonSelect
                                                            name={"teacher_id"}
                                                            placeholder={consultationDetails?.PreflightTeacher ? userFullName(consultationDetails?.PreflightTeacher) : schoolOptions?.find(x => x.id == filter?.school)?.name || "Select Teacher"}
                                                            option={teachersOptions || []}
                                                            isLoading={getTeacherListFetching}
                                                            isDisabled={editMode}
                                                        />
                                                    </Col>
                                                    <Col md="3" sm="6" className="my-2">
                                                        <label
                                                            htmlFor=""
                                                            className="form-label themeBlue fw-bold mb-0"
                                                        >
                                                            Grade
                                                        </label>
                                                        <CommonSelect
                                                            name="grade"
                                                            option={[{ label: "Select Grade", value: "" }, ...GRADE_OPTIONS]}
                                                            placeholder={consultationDetails?.grade || "Select Grade"}
                                                            onChange={(selected) => {
                                                                handleFilterChange("grade")(selected.value);
                                                            }}
                                                            isDisabled={editMode}
                                                        />
                                                    </Col>
                                                    <Col lg="12" sm="12">
                                                        {(!isEmpty(errors) && submitCount > 0) && (
                                                            <Row className="my-2">
                                                                <Col lg="12">
                                                                    <span className="error">{genericFormErrorMsg}</span>
                                                                </Col>
                                                            </Row>
                                                        )}
                                                        <div className="d-flex mt-4">
                                                            <Button
                                                                className="d-flex align-items-center justify-content-center commonBtn btn btn-primary"
                                                                type="submit"
                                                                disabled={createClassroomConsultationLoading || updateClassroomConsultationLoading}
                                                            >
                                                                {createClassroomConsultationLoading || updateClassroomConsultationLoading ? <LoadingIndicator color="#fff" /> : "Save"}
                                                            </Button>
                                                            <Button
                                                                className="mx-2 d-flex align-items-center justify-content-center commonBtn borderedBtn bg-transparent"
                                                                type="button"
                                                                onClick={() => navigate(CLASSROOM_SERVICES)}
                                                            >
                                                                Cancel
                                                            </Button>

                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Form>
                                        )}
                                    </Formik>
                                </Col>
                            </Row>
                        )}
                </section>
            </Container>
        </>
    )
};

export default AddClassRoomConsultation;